import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { AngularUser } from 'Angular/interfaces/angular-user.interface';
import { FeatureInfo } from 'EntitlementsApp/ts/interfaces/feature-info.interface';
import { FeatureValidationModelType } from 'EntitlementsApp/ts/interfaces/feature-validation.interface';

import { AjaxService } from 'Angular/services/core/ajax.service';
import { UrlsService } from 'Angular/services/core/urls.service';

import { FeatureNeededComponent } from 'EntitlementsApp/ts/features/feature-needed/feature-needed.component';

/**
 * Service that supports checking available and allowed features.
 */
@Injectable({
    providedIn: 'root',
})
export class FeatureService {
    USER: AngularUser = window.USER;

    allFeaturesAvailable: boolean = false;

    constructor(private ajaxService: AjaxService, private urlsService: UrlsService) {}

    /**
     * Returns true if user has access to all the input features.
     *
     * @param features features to check the user's access for
     */
    userHasFeatures(features: (string | undefined)[] | undefined): boolean {
        if (this.allFeaturesAvailable) return true;
        // If no features specified, allow access
        if (!features) return true;

        return features.every((feature: string | undefined) => {
            return feature === undefined || feature === '' || this.USER.features.includes(feature);
        });
    }

    /**
     * Returns true if user has access to any of the input features.
     *
     * @param features: features to check the user's access for
     */
    userHasSomeFeatures(features: string[]): boolean {
        if (this.allFeaturesAvailable) return true;

        return features.some((feature) => this.USER.features.includes(feature));
    }

    /**
     * Check if the user has access based on required features.
     *
     * This method provides a unified interface for checking feature access with support
     * for both AND logic (user must have ALL features) and OR logic (user needs ANY feature).
     * Similar to the backend MapFeaturesValidationService.user_has_features method.
     *
     * The method accepts an object with optional feature validation properties:
     * - features_needed: Array of feature strings (preferred)
     * - feature_needed: Single feature string (fallback if features_needed not provided)
     * - all_features_required: Boolean indicating AND (true) or OR (false) logic (defaults to true)
     *
     * @param validationObject - Object containing feature validation properties
     * @returns true if user has access based on the feature requirements, false otherwise
     *
     * @example
     * // Check if user has all features (AND logic)
     * userCanAccess({ features_needed: ['feature1', 'feature2'], all_features_required: true })
     *
     * @example
     * // Check if user has any feature (OR logic)
     * userCanAccess({ features_needed: ['feature1', 'feature2'], all_features_required: false })
     *
     * @example
     * // Fallback to single feature_needed property
     * userCanAccess({ feature_needed: 'feature1' })
     *
     * @example
     * // No features required - always returns true
     * userCanAccess({})
     * userCanAccess({ features_needed: [] })
     */
    userCanAccess(validationObject: FeatureValidationModelType): boolean {
        // If object is missing or has neither feature property, allow access
        if (!validationObject || (!validationObject.features_needed && !validationObject.feature_needed)) {
            return true;
        }

        // Extract features, falling back to feature_needed if features_needed not provided
        let features: string[] | undefined;
        if (validationObject.features_needed) {
            features = validationObject.features_needed;
        } else if (validationObject.feature_needed) {
            // Convert single feature string to array
            features = validationObject.feature_needed ? [validationObject.feature_needed] : undefined;
        }

        // If no features after extraction, allow access
        if (!features || features.length === 0) {
            return true;
        }

        // Extract all_features_required, defaulting to true
        const allRequired = validationObject.all_features_required ?? true;

        if (allRequired) {
            // AND logic: user must have ALL features
            return this.userHasFeatures(features);
        }
        // OR logic: user needs ANY feature
        return this.userHasSomeFeatures(features);
    }

    /**
     * Retrieves the first missing feature from an object with required features properties.
     *
     * This method is intended to be used after getting a `false` return from `userCanAccess`,
     * to identify which feature is missing.
     *
     * The method raises an error if:
     * - The object is empty
     * - The object has no properties with required features
     * - The user has access to all features
     *
     * @param validationObject - Object containing feature validation properties
     * @returns a string with the missing feature name
     *
     * @example
     * // Can retrieve missing features from `features_needed` property
     * const obj = { features_needed: ['feature1', 'missing_feature'] };
     * if (!userCanAccess(obj) {
     *     getFirstMissingFeature(obj); // 'missing_feature' is retrieved
     * }
     *
     * @example
     * // Only retrieves the first missing features from `features_needed` property
     * const obj = {
     *   features_needed: ['feature1', 'first_missing_feature', 'other_missing_feature'],
     * };
     * if (!userCanAccess(obj) {
     *     getFirstMissingFeature(obj); // 'first_missing_feature' is retrieved
     * }
     *
     * @example
     * // Can retrieve missing features from `feature_needed` property
     * const obj = { feature_needed: 'missing_feature' };
     * if (!userCanAccess(obj) {
     *     getFirstMissingFeature(obj); // 'missing_feature' is retrieved
     * }
     *
     * @example
     * // If `features_needed` property is present, `feature_needed` property is ignored
     * const obj = {
     *   feature_needed: 'missing_feature',
     *   features_needed: ['feature1', 'other_missing_feature'],
     * };
     * if (!userCanAccess(obj) {
     *     getFirstMissingFeature(obj); // 'other_missing_feature' is retrieved
     * }
     *
     * @example
     * // If no feature is missing, an error is thrown
     * const obj = { features_needed: ['feature1', 'feature2'] };
     * if (userCanAccess(obj) {
     *     getFirstMissingFeature(obj); // will throw an error
     * }
     *
     * @example
     * // If `features_needed` and `feature_needed` properties are not pressent, an error is thrown
     * const obj = { some_property };
     * getFirstMissingFeature(obj); // will throw an error
     *
     */
    getFirstMissingFeature(validationObject: FeatureValidationModelType): string {
        // If object is missing or has neither feature property, allow access
        if (!validationObject || (!validationObject.features_needed && !validationObject.feature_needed)) {
            throw new Error('Object is empty, or has no feature properties to validate.');
        }

        // Get the first missing feature
        let missing_feature: string | undefined;
        if (validationObject.features_needed) {
            missing_feature = validationObject.features_needed.find((f) => !this.userHasFeatures([f]));
        } else if (validationObject.feature_needed) {
            if (!this.userHasFeatures([validationObject.feature_needed])) {
                missing_feature = validationObject.feature_needed;
            }
        }

        if (missing_feature === undefined) {
            throw new Error('User has access to all features.');
        }
        return missing_feature;
    }

    /**
     * Filter items to only those the user has access to, and open the feature-needed modal
     * for the first missing feature if any items are inaccessible.
     *
     * @param items - Array of objects with feature validation properties
     * @param featureNeededModal - The FeatureNeededComponent to open if a feature is missing
     * @returns Array of items the user has access to
     */
    validateFeatureNeeded<T extends FeatureValidationModelType>(
        items: Array<T>,
        featureNeededModal: FeatureNeededComponent
    ): Array<T> {
        const validItems: Array<T> = [];
        let firstMissingFeature: string | null = null;
        items.forEach((item) => {
            if (this.userCanAccess(item)) {
                validItems.push(item);
            } else if (firstMissingFeature === null) {
                firstMissingFeature = this.getFirstMissingFeature(item);
            }
        });

        if (firstMissingFeature !== null) featureNeededModal.openModal(firstMissingFeature);

        return validItems;
    }

    allowAllFeaturesAccess(): void {
        // provides access to all features to current user
        // works as a one way switch and remains active for the current application
        this.allFeaturesAvailable = true;
    }

    getFeature(featureKey: string, nextUrl: string): Observable<FeatureInfo> {
        const urlParams = { featureKey };
        const postParams = { nextUrl };
        const url = this.urlsService.getUrl(this.urlsService.FEATURE_NEEDED_POPUP.GET_FEATURE, urlParams);
        return this.ajaxService.postFormEncodedDataObserveBody(url, postParams);
    }

    /**
     * Returns true if user is a member of the named client group.
     *
     * @param clientGroup client group to check the user's membership in
     */
    userInClientGroup(clientGroupName: string): boolean {
        return this.USER.customClassificationClientGroups.includes(clientGroupName);
    }
}
